import { hasCompletedOnboardingAtom } from '@heidi/growth/data';
import { DismissibleTooltip } from '@heidi/design-system/dismissible-tooltip';
import { useMutateUpdateUser } from '@/hooks/mutation/useMutateUpdateUser';
import { useQueryUser } from '@heidi/workspace/data';
import { sidebarOpenTabAtom, SidebarTab } from '@/atoms/persistedPreferencesAtoms';
import { useTranslations } from '@/utils/translations';
import { getHelpCenterUrl } from '@/helpers/getHelpCenterUrl';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';

interface ResearchModeTooltipProps {
  children: React.ReactNode;
}

export const ResearchModeTooltip = ({ children }: ResearchModeTooltipProps) => {
  const { t, locale } = useTranslations();
  const { data: user } = useQueryUser((data) => {
    if (!data) return undefined;
    return {
      has_seen_research_tooltip: data.has_seen_research_tooltip,
    };
  });

  const { mutate: updateUser } = useMutateUpdateUser();
  const sidebarOpenTab = useAtomValue(sidebarOpenTabAtom);
  const isSidebarOpen = sidebarOpenTab === SidebarTab.ASK_HEIDI_V2;
  const hasCompletedOnboarding = useAtomValue(hasCompletedOnboardingAtom);

  const [isOpen, setIsOpen] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);

  const hasNotSeenTooltip =
    user?.has_seen_research_tooltip === false ||
    user?.has_seen_research_tooltip === undefined;

  // Don't show the tooltip if user just completed onboarding in this session,
  // as they've already been introduced to Research mode through the onboarding flow
  const shouldShow =
    hasNotSeenTooltip && !hasDismissed && isSidebarOpen && !hasCompletedOnboarding;

  useEffect(() => {
    if (!isSidebarOpen) {
      setIsOpen(false);
    }
  }, [isSidebarOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open && isOpen) {
      updateUser({ has_seen_research_tooltip: true });
      setHasDismissed(true);
    }
    setIsOpen(open);
  };

  return (
    <DismissibleTooltip
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      shouldShow={shouldShow}
      title={t('askAI.researchModeTooltip.title')}
      description={t('askAI.researchModeTooltip.description')}
      learnMoreUrl={getHelpCenterUrl('articles/8974408-what-is-ask-heidi', locale)}
      learnMoreText={t('askAI.researchModeTooltip.learnMore')}
      dismissOnClickWithin="[data-chat-pane='true']"
      openDelay={600}
      side="top"
      align="start"
      alignOffset={20}
      sideOffset={8}
    >
      {children}
    </DismissibleTooltip>
  );
};
